import { useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';

/**
 * Returns the resize handler for `<Table onResize>`: optionally debounced, cancelled on
 * unmount, and `undefined` while disabled (the engine then doesn't report resizes).
 *
 * @param {Object} options
 * @param {Function} options.onResize - receives the engine resize changes `[{ id, value }]`
 * @param {number} [options.debounceMs=0] - debounce delay; 0 calls `onResize` right away
 * @param {boolean} [options.enabled=true]
 * @return {Function|undefined} handler to pass as `onResize`
 */
const useColumnResize = ({ onResize, debounceMs = 0, enabled = true }) => {
  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;

  const handler = useMemo(() => {
    const callOnResize = (changes) => onResizeRef.current?.(changes);
    return debounceMs ? debounce(callOnResize, debounceMs) : callOnResize;
  }, [debounceMs]);

  useEffect(() => () => handler.cancel?.(), [handler]);

  return enabled ? handler : undefined;
};

export default useColumnResize;
